'use client';

import { useCallback, useEffect, useMemo, type RefObject } from 'react';
import dynamic from 'next/dynamic';
import { pauseHomepageLenis, resumeHomepageLenis } from 'utils/homepageLenis';
import { getCompressedAssetUrl } from 'lib/config/assetUrls';
import { useModal } from 'components/shared/Modal';
import styles from './FilmVideoModal.module.css';

const DESKTOP_LAYOUT_QUERY = '(min-width: 992px)';
const ICON_PLAY = getCompressedAssetUrl(
  '/web_v2/site-static/home/emerald/icon-play.svg',
);

const DynamicVideoJS = dynamic(() => import('components/heroes/VideoJS'), {
  ssr: false,
});

const VideoModalPlayer = ({
  videoSrc,
  portrait,
  onUnmount,
}: {
  videoSrc: string;
  portrait: boolean;
  onUnmount: () => void;
}) => {
  useEffect(() => onUnmount, [onUnmount]);

  const videoOptions = useMemo(
    () => ({
      autoplay: true,
      controls: true,
      responsive: true,
      fluid: true,
      sources: [{ src: videoSrc, type: 'video/mp4' }],
      playsinline: true,
      preload: 'auto' as const,
      muted: false,
      loop: false,
      aspectRatio: portrait ? '9:16' : '16:9',
      html5: {
        vhs: { overrideNative: true },
        nativeAudioTracks: false,
        nativeVideoTracks: false,
      },
    }),
    [videoSrc, portrait],
  );

  return (
    <div
      className={`${styles.modalContent} ${
        portrait ? styles.modalPortrait : ''
      }`}
    >
      <DynamicVideoJS key={videoSrc} options={videoOptions} />
    </div>
  );
};

/**
 * Opens a film card's full cut, with sound and controls, in the site Modal.
 * The Modal unmounts its content on hide and owns the overflow lock, so this
 * only pauses Lenis and the card's inline loop while it is up. `mobile`
 * plays a portrait cut on phones; without it the landscape cut plays
 * everywhere.
 */
export function useFilmVideoModal(
  bannerVideoRef: RefObject<HTMLVideoElement | null>,
  src: { desktop: string; mobile?: string },
) {
  const modal = useModal();

  const onClose = useCallback(() => {
    resumeHomepageLenis();
    modal.setBg('');
    const video = bannerVideoRef.current;
    if (!video || !video.paused) return;
    const rect = video.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) {
      void video.play().catch(() => undefined);
    }
  }, [bannerVideoRef, modal]);

  return useCallback(() => {
    bannerVideoRef.current?.pause();
    pauseHomepageLenis();

    const portrait =
      !!src.mobile && !window.matchMedia(DESKTOP_LAYOUT_QUERY).matches;
    modal.setBg('dark');
    modal.setCloseButtonTheme('light');
    modal.set(
      <VideoModalPlayer
        videoSrc={portrait && src.mobile ? src.mobile : src.desktop}
        portrait={portrait}
        onUnmount={onClose}
      />,
    );
    modal.setPortal(true);
    modal.show();
  }, [bannerVideoRef, modal, onClose, src.desktop, src.mobile]);
}

/* The frosted play pill in a film card's corner. */
export const FilmWatchButton = ({
  onClick,
  label,
  buttonType,
}: {
  onClick: () => void;
  label: string;
  /** analytics `data-buttontype` */
  buttonType: string;
}) => (
  <button
    type="button"
    className={styles.watchPill}
    onClick={onClick}
    data-buttontype={buttonType}
    aria-label={label}
  >
    <img src={ICON_PLAY} alt="" width={24} height={24} aria-hidden />
  </button>
);
